import { Typography } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Figures in Geist Mono, so columns of numbers line up.
 *
 * The design system's way of doing this is the `…Mono` typography variants,
 * which carry identical metrics to their sans twins. This wrapper just picks the
 * right one, replacing 54 inline `fontFamily: 'var(--font-family-mono)'`
 * declarations — the `MonoText` helper ADR 0009 deferred until there were enough
 * pages to justify it.
 */
export type MonoTextProps = {
  children: ReactNode;
  /** Which mono variant to use. Defaults to body size. */
  size?: 'label' | 'body' | 'bodyBold' | 'h4' | 'h3';
  color?: string;
  component?: 'span' | 'div';
  /** Native tooltip — a read-only figure often has to explain who owns it. */
  title?: string;
  'data-automation-id'?: string;
};

const VARIANT = {
  label: 'labelMono',
  body: 'bodyRegularMono',
  bodyBold: 'bodyBoldMono',
  h4: 'h4Mono',
  h3: 'h3Mono',
} as const;

export const MonoText = ({
  children,
  size = 'body',
  color,
  component = 'span',
  title,
  'data-automation-id': automationId,
}: MonoTextProps) => (
  <Typography
    variant={VARIANT[size]}
    component={component}
    title={title}
    data-automation-id={automationId}
    sx={color ? { color } : undefined}
  >
    {children}
  </Typography>
);
